import {
    CommentEditorProvider,
    CommentsPanel as CommonCommentsPanel,
    CommentsUIStateProvider,
    CommentViewerProvider,
    Conversation,
    ICollaborationDataSource,
    ICollaborationMetadata,
    ICommentEditorProps,
    ICommentViewerProps,
    initialUIState,
    IVirtualizer,
} from '@cscglobal-tbs/collaboration-ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';
import { useParams } from 'react-router';
import { useIsFeatureEnabled } from '../../api/features/features';
import { appInsights } from '../../ApplicationInsightsService';
import { useExportConversations } from '../../hooks/useExportConversations';
import { CommentRichTextEditor } from '../comments/editor/CommentRichTextEditor';
import { CommentRichTextViewer } from '../comments/editor/CommentRichTextViewer';

export interface ICommentsPanelWrapperProps {
    dataSource: ICollaborationDataSource;
    metadata: ICollaborationMetadata;
    exportConversations?: ReturnType<typeof useExportConversations>;
    isOpen: boolean;
    onClose?: () => void;
    isCollaborationEnabled: boolean;
}

export const CommentsPanelWrapper = (props: ICommentsPanelWrapperProps) => {
    const { dataSource, metadata, exportConversations, isOpen } = props;

    const didRefetch = React.useRef(false);

    if (isOpen && !didRefetch.current) {
        //whenever we open the comments panel, we need to refetch the items as they could be stale
        dataSource.refetchItems();
        didRefetch.current = true;
    }

    const conversations: Conversation[] = dataSource.items;

    const commentId = useParams().commentId;
    const initialCommentsUIState = React.useMemo(() => {
        const highlightedCommentId = commentId ? commentId.toLowerCase() : null;
        return {
            ...initialUIState,
            highlightedCommentId,
            selectedCommentId: highlightedCommentId,
            expandedResolvedConversationId: highlightedCommentId,
        };
    }, [commentId]);

    // Create scroll element ref for virtualizer
    const scrollElementRef = React.useRef<HTMLDivElement>(null);

    // Calculate total items count for virtualization
    const totalItemsCount = React.useMemo(() => {
        if (!conversations) return 0;
        //if there are resolved conversations, we need to add one more item to the virtualizer to account for the resolved conversation header
        if (conversations.some((conversation) => conversation.resolved)) {
            return conversations.length + 1;
        }
        return conversations.length;
    }, [conversations]);

    const tanstackVirtualizer = useVirtualizer({
        count: totalItemsCount,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: () => 250,
        overscan: 5,
        measureElement: (element) => {
            return element?.getBoundingClientRect().height;
        },
    });

    // Create virtualizer adapter that implements IVirtualizer interface
    const virtualizer: IVirtualizer = React.useMemo(
        () => ({
            getVirtualItems: () => {
                const virtualItems = tanstackVirtualizer.getVirtualItems().map((item) => ({
                    index: item.index,
                    start: item.start,
                    end: item.end,
                    size: item.size,
                    key: item.key.toString(),
                }));
                return virtualItems;
            },
            getTotalSize: () => {
                const totalSize = tanstackVirtualizer.getTotalSize();
                return totalSize;
            },
            scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end'; behavior?: 'auto' | 'smooth' }) => {
                tanstackVirtualizer.scrollToIndex(index, {
                    align: options?.align || 'center',
                    behavior: 'auto',
                });
            },
            measureElement: (element: HTMLElement) => {
                const height = element.getBoundingClientRect().height;
                tanstackVirtualizer.measureElement(element);
                return height;
            },
            scrollElementRef,
        }),
        [tanstackVirtualizer]
    );

    // Custom editor context for the library component
    const customEditorContext = {
        renderEditor: (editorProps: ICommentEditorProps) => {
            return <CommentRichTextEditor {...editorProps} />;
        },
    };

    // Custom viewer context for the library component
    const customViewerContext = {
        renderViewer: (viewerProps: ICommentViewerProps) => {
            return <CommentRichTextViewer {...viewerProps} />;
        },
    };

    const handleClose = () => {
        exportConversations?.clearActiveMessage?.();
        didRefetch.current = false;
        if (props.onClose) {
            props.onClose();
        }
    };

    const { data: isCommentFilteringEnabled } = useIsFeatureEnabled({
        featureFlagName: 'CommentFilteringEnabled',
    });

    return (
        <CommentsUIStateProvider commentsUIState={initialCommentsUIState}>
            <CommentEditorProvider value={customEditorContext}>
                <CommentViewerProvider value={customViewerContext}>
                    <CommonCommentsPanel
                        isOpen={isOpen}
                        onClose={handleClose}
                        dataSource={dataSource}
                        metadata={metadata}
                        isCollaborationEnabled={props.isCollaborationEnabled}
                        showExportExcelButton={exportConversations?.exportConversationsToExcel ? true : false}
                        showSearchButton={isCommentFilteringEnabled ?? false}
                        onExportConversationsToExcel={exportConversations?.exportConversationsToExcel}
                        onDismissExportError={exportConversations?.clearActiveMessage}
                        exportError={exportConversations?.activeMessage?.message}
                        virtualizer={virtualizer}
                        onTelemetryEvent={(event: string, data?: any) => {
                            appInsights?.trackEvent({ name: event, properties: data });
                        }}
                    />
                </CommentViewerProvider>
            </CommentEditorProvider>
        </CommentsUIStateProvider>
    );
};
